import React, { useEffect, useRef } from 'react';
import { OnMount, BeforeMount, Monaco } from '@monaco-editor/react';
import type * as monacoEditor from 'monaco-editor';
import { useTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

import CodeEditor from '../CodeEditor';

import {
  VARIABLE_TOKEN_ID,
  PROMPT_LANGUAGE_THEME_ID,
  PROMPT_LANGUAGE_THEME,
  MONACO_EDITOR_OPTIONS,
  PromptLanguage,
} from './const';

const getLanguageSuggestions = (language: PromptLanguage, t: TFunction) => {
  const COPILOT_CONTEXT_SUGGESTION = {
    label: '{copilot_context}',
    description: t('Prompt editor: Suggestions: {copilot_context} description'),
  };

  const CONTEXT_STR_SUGGESTION = {
    label: '{context_str}',
    description: t('Prompt editor: Suggestions: {context_str} description'),
  };

  const QUERY_STR_SUGGESTION = {
    label: '{query_str}',
    description: t('Prompt editor: Suggestions: {query_str} description'),
  };

  const NUM_QUERIES_SUGGESTION = {
    label: '{num_queries}',
    description: t('Prompt editor: Suggestions: {num_queries} description'),
  };

  const CHAT_HISTORY_SUGGESTION = {
    label: '{chat_history}',
    description: t('Prompt editor: Suggestions: {chat_history} description'),
  };

  const QUERY_SUGGESTION = {
    label: '{query}',
    description: t('Prompt editor: Suggestions: {query} description'),
  };

  const CHUNK_COUNT_SUGGESTION = {
    label: '{chunk_count}',
    description: t('Prompt editor: Suggestions: {chunk_count} description'),
  };

  switch (language) {
    case PromptLanguage.MASKING:
      return [];
    case PromptLanguage.RERANK:
      return [CONTEXT_STR_SUGGESTION, QUERY_STR_SUGGESTION];
    case PromptLanguage.RESPONSE_GENERATION:
      return [COPILOT_CONTEXT_SUGGESTION, CONTEXT_STR_SUGGESTION];
    case PromptLanguage.PARAPHRASING:
      return [NUM_QUERIES_SUGGESTION, COPILOT_CONTEXT_SUGGESTION, CHAT_HISTORY_SUGGESTION, QUERY_SUGGESTION];
    case PromptLanguage.ROUTING:
      return [];
    case PromptLanguage.SPLITTING:
      return [CHUNK_COUNT_SUGGESTION];

    default:
      return [];
  }
};

export interface IPromptEditorProps {
  value: string;
  isFullscreen: boolean;
  readOnly?: boolean;
  promptLanguage: PromptLanguage;
  onChange?: (value: string) => void;
  onBlur?: () => void;
}

const PromptEditor: React.FC<IPromptEditorProps> = ({
  value,
  isFullscreen,
  readOnly = false,
  promptLanguage,
  onChange,
  onBlur,
}) => {
  const { t } = useTranslation();

  const editorRef = useRef<monacoEditor.editor.IStandaloneCodeEditor | null>(null);
  const onChangeRef = useRef(onChange);
  const valueRef = useRef(value);
  valueRef.current = value;

  useEffect(() => {
    onChangeRef.current = onChange;
  }, [onChange]);

  const beforeEditorMount: BeforeMount = (monaco: Monaco) => {
    if (
      promptLanguage !== PromptLanguage.PLAIN_TEXT &&
      !monaco.languages.getLanguages().some((lang) => lang.id === promptLanguage)
    ) {
      monaco.languages.register({ id: promptLanguage });

      monaco.languages.setMonarchTokensProvider(promptLanguage, {
        tokenizer: {
          root: [
            [/\{[^\s}]*\}?/, VARIABLE_TOKEN_ID], // any substring starting from "{" until the next space or "}"
          ],
        },
      });

      monaco.languages.registerCompletionItemProvider(promptLanguage, {
        triggerCharacters: ['{'],

        provideCompletionItems: (
          model: monacoEditor.editor.ITextModel,
          position: monacoEditor.Position,
          context: monacoEditor.languages.CompletionContext
        ) => {
          // Indicates "{" was typed in already so it should be replaced to prevent double "{{" after completion insert.
          const shouldReplaceBrace =
            context.triggerKind === monaco.languages.CompletionTriggerKind.TriggerCharacter &&
            context.triggerCharacter === '{';

          const defaultWord = model.getWordUntilPosition(position);
          const replaceRange = new monaco.Range(
            position.lineNumber,
            shouldReplaceBrace ? defaultWord.startColumn - 1 : defaultWord.startColumn,
            position.lineNumber,
            defaultWord.endColumn
          );

          const makeItem = (label: string, description?: string) => ({
            label,
            kind: monaco.languages.CompletionItemKind.Snippet,
            insertText: `${label}`, // NOTE: label should contain {} decorators
            insertTextRules: monaco.languages.CompletionItemInsertTextRule.InsertAsSnippet,
            detail: description,
            range: replaceRange,
          });

          return {
            suggestions: getLanguageSuggestions(promptLanguage, t).map((s) => makeItem(s.label, s.description)),
          };
        },
      });
    }

    monaco.editor.defineTheme(PROMPT_LANGUAGE_THEME_ID, PROMPT_LANGUAGE_THEME);
  };

  useEffect(() => {
    if (!editorRef.current) return;
    const currentValue = editorRef.current.getModel()?.getValue();
    if (value !== currentValue) {
      editorRef.current.setValue(value);
    }
  }, [value]);

  const onEditorMount: OnMount = (editor, monaco) => {
    editorRef.current = editor;

    const model = monaco.editor.createModel(valueRef.current, promptLanguage);
    editor.setModel(model);
    monaco.editor.setTheme(PROMPT_LANGUAGE_THEME_ID);

    model.onDidChangeContent(() => {
      onChangeRef.current?.(model.getValue());
    });
  };

  return (
    <CodeEditor
      beforeEditorMount={beforeEditorMount}
      codeLanguage={promptLanguage}
      defaultValue={value}
      isFullscreen={isFullscreen}
      options={MONACO_EDITOR_OPTIONS}
      readOnly={readOnly}
      onBlur={onBlur}
      onEditorMount={onEditorMount}
    />
  );
};

export default PromptEditor;
